import React from 'react';
import ReactMarkdown from 'react-markdown';
import remarkGfm from 'remark-gfm';

const BASE_COMPONENTS = {
  h1: ({ node, ...p }) => (
    <h1 className="mt-8 mb-4 text-2xl font-extrabold tracking-tight text-ink" {...p} />
  ),
  h2: ({ node, ...p }) => (
    <h2 className="mt-8 mb-3 text-xl font-extrabold tracking-tight text-ink" {...p} />
  ),
  h3: ({ node, ...p }) => (
    <h3 className="mt-6 mb-2 text-base font-extrabold tracking-tight text-ink" {...p} />
  ),
  h4: ({ node, ...p }) => (
    <h4 className="mt-4 mb-2 font-mono text-xs font-normal uppercase tracking-[0.16em] text-mute" {...p} />
  ),
  p: ({ node, ...p }) => <p className="my-3 text-ink" {...p} />,
  ul: ({ node, ...p }) => <ul className="my-3 list-disc pl-5 marker:text-space" {...p} />,
  ol: ({ node, ...p }) => <ol className="my-3 list-decimal pl-5 marker:text-mute" {...p} />,
  li: ({ node, children, ...p }) => (
    <li className="text-ink" {...p}>
      {children}
    </li>
  ),
  strong: ({ node, ...p }) => <strong className="font-extrabold text-ink" {...p} />,
  em: ({ node, ...p }) => <em className="italic text-mute" {...p} />,
  a: ({ node, ...p }) => (
    <a
      className="text-talent underline underline-offset-2 hover:text-talent/80"
      target="_blank"
      rel="noreferrer"
      {...p}
    />
  ),
  hr: () => <hr className="my-6 border-0" />,
  blockquote: ({ node, ...p }) => (
    <blockquote
      className="my-4 rounded-r-[12px] border-l-2 border-space bg-space/[0.06] py-3 pl-4 pr-4 text-ink"
      {...p}
    />
  ),
  code: ({ node, className, children, ...p }) => {
    const isBlock = typeof className === 'string' && /language-/.test(className);
    return isBlock ? (
      <code className="font-mono text-[13px] text-ink" {...p}>
        {children}
      </code>
    ) : (
      <code
        className="rounded border border-shell-border bg-paper-2 px-1.5 py-0.5 font-mono text-[13px] text-space"
        {...p}>
        {children}
      </code>
    );
  },
  pre: ({ node, ...p }) => (
    <pre
      className="my-4 overflow-x-auto rounded-[12px] border border-shell-border bg-shell-card p-4 text-[13px] shadow-soft"
      {...p}
    />
  ),
  table: ({ node, ...p }) => (
    <div className="my-5 overflow-hidden rounded-[12px] bg-shell-card shadow-soft">
      <div className="overflow-x-auto [&::-webkit-scrollbar]:h-1.5 [&::-webkit-scrollbar-thumb]:rounded-full [&::-webkit-scrollbar-thumb]:bg-mute [&::-webkit-scrollbar-track]:bg-transparent">
        <table className="w-full border-collapse text-sm" {...p} />
      </div>
    </div>
  ),
  thead: ({ node, ...p }) => <thead className="bg-paper-2" {...p} />,
  th: ({ node, ...p }) => (
    <th
      className="whitespace-nowrap border border-[rgba(157,157,157,0.3)] px-4 py-2.5 text-left font-extrabold text-ink"
      {...p}
    />
  ),
  td: ({ node, ...p }) => (
    <td className="whitespace-nowrap border border-[rgba(157,157,157,0.3)] px-4 py-2.5 align-top text-ink" {...p} />
  ),
  tr: ({ node, ...p }) => <tr className="even:bg-paper/40" {...p} />,
};

/**
 * Markdown renderer for LYVE LABS surfaces (docs + chat).
 * Styled with Tailwind theme tokens. Accepts `components` overrides
 * and `rehypePlugins` (e.g. rehypeRaw for raw <mark> tags).
 */
export default function MarkdownRenderer({ children, components = {}, rehypePlugins = [] }) {
  return (
    <div className="text-[16px] leading-[1.55] text-ink">
      <ReactMarkdown
        remarkPlugins={[remarkGfm]}
        rehypePlugins={rehypePlugins}
        components={{ ...BASE_COMPONENTS, ...components }}>
        {children}
      </ReactMarkdown>
    </div>
  );
}
